import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import Modal from './ui/Modal';
import { IconX } from '@tabler/icons-react';
import BACKEND_URL from '../config';

interface DocumentTextModalProps {
    isOpen: boolean;
    onClose: () => void;
    documentId: string | null;
    documentName?: string;
}

const DocumentTextModal: React.FC<DocumentTextModalProps> = ({
    isOpen,
    onClose,
    documentId,
    documentName,
}) => {
    const [text, setText] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen || !documentId) return;

        let cancelled = false;
        setLoading(true);
        setError(null);
        setText(null);

        fetch(`${BACKEND_URL}/vnog/get_document_text/${documentId}/`, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'X-CSRFToken': Cookies.get('csrftoken') || '',
            },
        })
            .then(async (response) => {
                if (cancelled) return;
                if (response.status === 404) {
                    setError('Geen geparseerde tekst beschikbaar voor dit document.');
                    return;
                }
                if (!response.ok) {
                    setError(`Kon geparseerde tekst niet ophalen (status ${response.status}).`);
                    return;
                }
                const data = await response.json();
                if (cancelled) return;
                setText(typeof data.text === 'string' ? data.text : '');
            })
            .catch((e: unknown) => {
                if (cancelled) return;
                setError(e instanceof Error ? e.message : 'Onbekende fout bij ophalen tekst.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isOpen, documentId]);

    return (
        <Modal isOpen={isOpen} onRequestClose={onClose}>
            <div className="w-[90vw] h-[90vh] bg-white rounded-lg flex flex-col">
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-xl font-semibold">
                        {documentName ? `Geparseerde tekst — ${documentName}` : 'Geparseerde tekst'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                        aria-label="Close"
                    >
                        <IconX size={24} stroke={1.5} />
                    </button>
                </div>
                <div className="flex-1 p-4 overflow-y-auto">
                    {loading && (
                        <div className="flex items-center justify-center h-full text-gray-500">
                            Tekst laden…
                        </div>
                    )}
                    {!loading && error && (
                        <div className="flex items-center justify-center h-full text-gray-500">
                            {error}
                        </div>
                    )}
                    {!loading && !error && text !== null && (
                        <pre className="whitespace-pre-wrap text-sm font-mono text-gray-800 bg-gray-50 rounded p-4">
                            {text}
                        </pre>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default DocumentTextModal;
